import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateFilter.css';
import './DateFilter.mobile.css';
import dateUtils from '../utils/dateUtils';

const DateFilter = ({ onDateChange, initialStartDate = new Date(), initialEndDate = new Date() }) => {
    const [isOpen, setIsOpen] = useState(false);

    const getInitialLabel = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const start = new Date(initialStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(initialEndDate);
        end.setHours(23, 59, 59, 999);
        
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        
        if (start.getTime() === today.getTime() && end.getTime() === todayEnd.getTime()) {
            return 'Today';
        }
        
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        firstDay.setHours(0, 0, 0, 0);
        if (start.getTime() === firstDay.getTime() && end.getTime() === todayEnd.getTime()) {
            return 'This Month';
        }
        
        return 'Custom Range';
    };

    const [selectedRange, setSelectedRange] = useState({
        label: getInitialLabel(),
        startDate: initialStartDate,
        endDate: initialEndDate
    });
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const [customStartDate, setCustomStartDate] = useState(initialStartDate);
    const [customEndDate, setCustomEndDate] = useState(initialEndDate);

    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                if (!event.target.closest('.react-datepicker')) {
                    setIsOpen(false);
                    setShowCustomPicker(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatDate = (date) => {
        return dateUtils.formatDate(date);
    };

    const handlePresetClick = (preset) => {
        const today = dateUtils.getChicagoToday();
        let start = new Date(today);
        let end = new Date(today);

        switch (preset) {
            case 'Today':
                break;
            case 'Yesterday':
                start.setDate(today.getDate() - 1);
                end.setDate(today.getDate() - 1);
                break;
            case 'Last 7 Days':
                start.setDate(today.getDate() - 6);
                break;
            case 'Last 30 Days':
                start.setDate(today.getDate() - 29);
                break;
            case 'This Month':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'Last Month':
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'This Month Last Year':
                start = new Date(today.getFullYear() - 1, today.getMonth(), 1);
                end = new Date(today.getFullYear() - 1, today.getMonth() + 1, 0);
                break;
            case 'This Year':
                start = new Date(today.getFullYear(), 0, 1);
                break;
            case 'Last Year':
                start = new Date(today.getFullYear() - 1, 0, 1);
                end = new Date(today.getFullYear() - 1, 11, 31);
                break;
            case 'Current Financial Year':
                // Assuming financial year starts April 1st
                if (today.getMonth() >= 3) {
                    start = new Date(today.getFullYear(), 3, 1);
                    end = new Date(today.getFullYear() + 1, 2, 31);
                } else {
                    start = new Date(today.getFullYear() - 1, 3, 1);
                    end = new Date(today.getFullYear(), 2, 31);
                }
                break;
            case 'Last Financial Year':
                if (today.getMonth() >= 3) {
                    start = new Date(today.getFullYear() - 1, 3, 1);
                    end = new Date(today.getFullYear(), 2, 31);
                } else {
                    start = new Date(today.getFullYear() - 2, 3, 1);
                    end = new Date(today.getFullYear() - 1, 2, 31);
                }
                break;
            case 'Custom Range':
                setShowCustomPicker(true);
                return;
            default:
                break;
        }

        // Set time to start of day and end of day
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        setSelectedRange({ label: preset, startDate: start, endDate: end });
        setIsOpen(false);
        if (onDateChange) onDateChange({ start, end });
    };

    const handleCustomDateChange = (dates) => {
        let [start, end] = dates;

        // If start is selected, set time to 00:00:00
        if (start) {
            start = new Date(start);
            start.setHours(0, 0, 0, 0);
        }

        // If end is selected, set time to 23:59:59
        if (end) {
            end = new Date(end);
            end.setHours(23, 59, 59, 999);
        }

        setCustomStartDate(start);
        setCustomEndDate(end);

        if (start && end) {
            setSelectedRange({ label: 'Custom Range', startDate: start, endDate: end });
            // Don't close immediately to let user see selection
            if (onDateChange) onDateChange({ start, end });
        }
    };

    const displayText = `${formatDate(selectedRange.startDate)} ~ ${formatDate(selectedRange.endDate)}`;

    return (
        <div className="date-filter-container" ref={dropdownRef}>
            <button className="date-filter-btn" onClick={() => setIsOpen(!isOpen)}>
                <span className="calendar-icon">📅</span>
                <span className="date-text">{displayText}</span>
            </button>

            {isOpen && (
                <div className="date-filter-menu" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {!showCustomPicker ? (
                        <>
                            <div className={`menu-item ${selectedRange.label === 'Today' ? 'active' : ''}`} onClick={() => handlePresetClick('Today')}>Today</div>
                            <div className={`menu-item ${selectedRange.label === 'Yesterday' ? 'active' : ''}`} onClick={() => handlePresetClick('Yesterday')}>Yesterday</div>
                            <div className={`menu-item ${selectedRange.label === 'Last 7 Days' ? 'active' : ''}`} onClick={() => handlePresetClick('Last 7 Days')}>Last 7 Days</div>
                            <div className={`menu-item ${selectedRange.label === 'Last 30 Days' ? 'active' : ''}`} onClick={() => handlePresetClick('Last 30 Days')}>Last 30 Days</div>
                            <div className={`menu-item ${selectedRange.label === 'This Month' ? 'active' : ''}`} onClick={() => handlePresetClick('This Month')}>This Month</div>
                            <div className={`menu-item ${selectedRange.label === 'Last Month' ? 'active' : ''}`} onClick={() => handlePresetClick('Last Month')}>Last Month</div>
                            <div className={`menu-item ${selectedRange.label === 'This Month Last Year' ? 'active' : ''}`} onClick={() => handlePresetClick('This Month Last Year')}>This month last year</div>
                            <div className={`menu-item ${selectedRange.label === 'This Year' ? 'active' : ''}`} onClick={() => handlePresetClick('This Year')}>This Year</div>
                            <div className={`menu-item ${selectedRange.label === 'Last Year' ? 'active' : ''}`} onClick={() => handlePresetClick('Last Year')}>Last Year</div>
                            <div className={`menu-item ${selectedRange.label === 'Current Financial Year' ? 'active' : ''}`} onClick={() => handlePresetClick('Current Financial Year')}>Current financial year</div>
                            <div className={`menu-item ${selectedRange.label === 'Last Financial Year' ? 'active' : ''}`} onClick={() => handlePresetClick('Last Financial Year')}>Last financial year</div>
                            <div className={`menu-item ${selectedRange.label === 'Custom Range' ? 'active' : ''}`} onClick={() => handlePresetClick('Custom Range')}>Custom Range</div>
                        </>
                    ) : (
                        <div className="custom-picker-container">
                            <button className="back-btn" onClick={() => setShowCustomPicker(false)}>← Back</button>
                            <DatePicker
                                selected={customStartDate}
                                onChange={handleCustomDateChange}
                                startDate={customStartDate}
                                endDate={customEndDate}
                                selectsRange
                                inline
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DateFilter;
